import { clsx } from 'clsx'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { LANGUAGES, LANGUAGE_LABEL } from '@/shared/i18n/types'
import { useAppStore } from '@/shared/stores/appStore'
import type { Language } from '@/shared/i18n/types'
import type { Theme } from '@/shared/stores/appStore'
import styles from './PreferencesControl.module.css'

const THEMES: readonly Theme[] = ['system', 'light', 'dark'] as const

type PreferencesControlProps = {
  /** Denser layout for the header dropdown. */
  isCompact?: boolean
  className?: string | undefined
}

/**
 * Language and theme, chosen together — they are the two "how this looks to me"
 * settings and a player who switches one usually wants the other nearby.
 *
 * Both are client-only for now. A server-backed `UserProfile.Language` is only
 * needed once notification and email copy has to follow the choice.
 */
export function PreferencesControl({ isCompact = false, className }: PreferencesControlProps) {
  const { t } = useTranslation()
  const language = useAppStore((s) => s.language)
  const setLanguage = useAppStore((s) => s.setLanguage)
  const theme = useAppStore((s) => s.theme)
  const setTheme = useAppStore((s) => s.setTheme)

  const themeLabel: Record<Theme, string> = {
    system: t('prefs.theme.system'),
    light:  t('prefs.theme.light'),
    dark:   t('prefs.theme.dark'),
  }

  return (
    <div className={clsx(styles.control, isCompact && styles.isCompact, className)}>
      <div className={styles.group}>
        <span className={styles.legend} id="prefs-language">
          {t('prefs.language')}
        </span>
        <div className={styles.segments} role="group" aria-labelledby="prefs-language">
          {LANGUAGES.map((option: Language) => (
            <button
              key={option}
              type="button"
              className={clsx(styles.segment, option === language && styles.isActive)}
              aria-pressed={option === language}
              onClick={() => setLanguage(option)}
            >
              {LANGUAGE_LABEL[option]}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.group}>
        <span className={styles.legend} id="prefs-theme">
          {t('prefs.theme')}
        </span>
        <div className={styles.segments} role="group" aria-labelledby="prefs-theme">
          {THEMES.map((option) => (
            <button
              key={option}
              type="button"
              className={clsx(styles.segment, option === theme && styles.isActive)}
              aria-pressed={option === theme}
              onClick={() => setTheme(option)}
            >
              {themeLabel[option]}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
